/**
 * `resolveHelpLink` — classify a markdown link href from the help corpus into an
 * in-app navigation intent.
 *
 * The corpus pages link to each other with RELATIVE `.md` links, e.g.
 *   [Settings](settings.md)
 *   [Teams guide](../teams-integration.md)      (from a site page)
 *   [Screening](site/screening-and-fit-scores.md) (from a guide page)
 *   [Home](index.md)
 * plus external `https://…` / `mailto:…` links and same-page `#anchor` links.
 *
 * This resolver is PURE and slug-set-driven so it is unit-testable and so the
 * renderer can build a tenant-scoped route (`/{slug}/help/{docSlug}`) without
 * the resolver needing to know the tenant base.
 *
 * SECURITY: mirrors the `EvaluationMarkdown` posture — only `http:`, `https:`,
 * `mailto:`, in-app, and anchor hrefs are ever emitted as links. Any other
 * scheme (`javascript:`, `data:`, `vbscript:`, `file:`, …) resolves to
 * `{ kind: "plain" }` so it renders as inert text with no href in the DOM.
 */

type ResolvedHelpLink =
  | { kind: "internal"; slug: string; hash: string }
  | { kind: "external"; href: string }
  | { kind: "anchor"; href: string }
  | { kind: "plain" };

/** The landing page (`index.md`) is served at `/help`, not `/help/index`. */
export const HELP_INDEX_SLUG = "index";

function isExternalSafe(href: string): boolean {
  return /^(?:https?:|mailto:)/i.test(href);
}

/**
 * Extract the doc slug + hash fragment from a relative `.md` link.
 * Handles `foo.md`, `./foo.md`, `../foo.md`, `site/foo.md`, `foo.md#frag`.
 * Returns null when the target is not a `.md` path.
 */
function parseMdTarget(href: string): { slug: string; hash: string } | null {
  const hashIdx = href.indexOf("#");
  const hash = hashIdx >= 0 ? href.slice(hashIdx) : "";
  const path = hashIdx >= 0 ? href.slice(0, hashIdx) : href;
  if (!/\.md$/i.test(path)) return null;
  // Basename without the `.md` extension.
  const segments = path.split("/");
  const last = segments[segments.length - 1] ?? "";
  const slug = last.replace(/\.md$/i, "");
  if (slug.length === 0) return null;
  return { slug, hash };
}

/**
 * @param href      the raw markdown link target
 * @param knownSlugs the set of doc slugs that exist in the rendered corpus
 */
export function resolveHelpLink(
  href: string | undefined,
  knownSlugs: ReadonlySet<string>,
): ResolvedHelpLink {
  if (typeof href !== "string" || href.length === 0) return { kind: "plain" };

  // Same-page anchor.
  if (href.startsWith("#")) return { kind: "anchor", href };

  // External http(s)/mailto — safe schemes only.
  if (isExternalSafe(href)) return { kind: "external", href };

  // Relative `.md` inter-page link.
  const target = parseMdTarget(href);
  if (target) {
    // Only rewrite to a route we can actually serve. Unknown targets (e.g.
    // internal admin-only setup docs not in the published corpus) render as
    // inert text rather than sending a user to a not-found.
    if (target.slug === HELP_INDEX_SLUG || knownSlugs.has(target.slug)) {
      return { kind: "internal", slug: target.slug, hash: target.hash };
    }
    return { kind: "plain" };
  }

  // Anything else — unknown/unsafe scheme or a non-.md relative path we do not
  // publish. Render inert (no href reaches the DOM).
  return { kind: "plain" };
}

/**
 * Build the in-app route for an internal help link, given the tenant base
 * (`/{slug}`). `index` maps to the help landing `/{base}/help`.
 */
export function internalHelpHref(
  base: string,
  slug: string,
  hash: string,
): string {
  const path =
    slug === HELP_INDEX_SLUG ? `${base}/help` : `${base}/help/${slug}`;
  return `${path}${hash}`;
}
